"use client";
import { getGlobalSearchFilter } from "@/store/feature/ai-course-finder/aiCourseApi";
import { setAiCourseFinderCountry } from "@/store/feature/ai-course-finder/aiCourseFinderSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { FilteredDataType } from "@/store/types/aiCourseFinder";
import { capitalFirstLetter } from "@/utils/capitalFirstLetter";
import { getClosestFutureDate } from "@/utils/getClosestFutureDate";
import React, { useEffect, useMemo } from "react";
import { Close, EducationCap, PreferencesMenu } from "../Icons";
import SelectInput from "./SelectInput";
import { useResetPageToOne } from "../ResetPageToOne";

interface FilterSideBarProps {
  open: boolean;
  filteredData: FilteredDataType;
  activeTab: string;
  setFilteredData: React.Dispatch<React.SetStateAction<FilteredDataType>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  from?: string;
  showLevel?: boolean;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  scholarshipOptions?: { title: string; value: string }[];
  showScholarShip?: boolean;
}

const SkeletonPills = () => (
  <>
    {[1, 2, 3, 4].map((n) => (
      <div
        key={n}
        className="h-[38px] sm:h-[47px] w-24 bg-[#E5E7EB] animate-pulse rounded-full"
      />
    ))}
  </>
);

const FilterSideBar = ({
  open,
  setOpen,
  activeTab,
  filteredData,
  setFilteredData,
  from,
  showLevel = true,
  setPage,
  scholarshipOptions,
  showScholarShip,
}: FilterSideBarProps) => {
  const resetPage = useResetPageToOne();
  const dispatch = useAppDispatch();
  const { countryList } = useAppSelector((state) => state.country);

  const {
    country,
    courseFilterOptions,
    courseSpecialisationOptions,
    courseDuration,
    courseStream,
    specialisationCategory,
    intakeOptions,
  } = useAppSelector((state) => state.aiCourseFinder);
  const newCountry = useMemo(() => {
    return countryList?.length > 0
      ? countryList.map((item) => ({
          id: item.country_id,
          title: item.country_name,
        }))
      : [];
  }, [countryList]);

  useEffect(() => {
    dispatch(getGlobalSearchFilter());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newCourseDuration = useMemo(() => {
    if (!courseDuration || courseDuration.length === 0) return [];

    interface GetTotalMonths {
      (str: string): number;
    }

    const getTotalMonths: GetTotalMonths = (str) => {
      if (!str) return 0;

      str = str
        .toLowerCase()
        .replace(/years?/g, "year")
        .replace(/months?/g, "month")
        .replace(/\s+/g, " ");

      let years = 0,
        months = 0;

      const yearMatch = str.match(/(\d+)\s*year/);
      const monthMatch = str.match(/(\d+)\s*month/);

      if (yearMatch) years = parseInt(yearMatch[1]);
      if (monthMatch) months = parseInt(monthMatch[1]);

      return years * 12 + months;
    };

    const uniqueMonths = [
      ...new Set(courseDuration.map((item) => getTotalMonths(item.duration))),
    ].sort((a, b) => a - b);

    if (uniqueMonths.length === 0) return [];

    const highestMonths = Math.max(...uniqueMonths);
    const highestYear = Math.ceil(highestMonths / 12);

    const rangesArr = [];
    for (let i = 0; i < highestYear; i++) {
      rangesArr.push({
        id: i,
        title: `${i}-${i + 1} Year`,
      });
    }

    return rangesArr;
  }, [courseDuration]);

  const newCourseSpecialisationOptions = useMemo(() => {
    return [
      { id: 0, title: "All", value: "" }, // Add "All" at the beginning
      ...(courseSpecialisationOptions?.map(
        ({ course_specialisation }, index) => ({
          id: index + 1,
          title: course_specialisation ?? "",
          value: course_specialisation ?? "",
        }),
      ) || []),
    ];
  }, [courseSpecialisationOptions]);

  const newIntakeOption = useMemo(() => {
    if (!intakeOptions || intakeOptions.length === 0) return [];

    const filtered = intakeOptions.filter(
      (item) => item.status?.[0]?.toLowerCase() === "open",
    );

    const uniqueMonths = Array.from(
      new Set(
        filtered
          .map((item) => item.month?.[0])
          .filter((month): month is string => typeof month === "string"),
      ),
    );

    uniqueMonths.sort((a, b) => {
      const [monthA, yearA] = a.split("/").map(Number);
      const [monthB, yearB] = b.split("/").map(Number);

      const dateA = new Date(yearA, monthA - 1, 1);
      const dateB = new Date(yearB, monthB - 1, 1);

      return dateA.getTime() - dateB.getTime();
    });

    return uniqueMonths.map((month) => ({
      id: month,
      title: getClosestFutureDate([month]),
    }));
  }, [intakeOptions]);

  const isUniversityTab =
    activeTab === "universities" || activeTab === "colleges";

  const handleStreamSelection = (streamValue: string) => {
    const currentStreams = Array.isArray(filteredData.courseStreamFilter)
      ? filteredData.courseStreamFilter
      : [];

    let updatedStreams: string[];

    if (currentStreams.includes(streamValue)) {
      updatedStreams = currentStreams.filter(
        (stream) => stream !== streamValue,
      );
    } else {
      updatedStreams = [...currentStreams, streamValue];
    }

    setFilteredData({
      ...filteredData,
      courseStreamFilter: updatedStreams,
    });
    setPage?.(1);
    resetPage();
    closeSidebar();
  };

  const isStreamSelected = (streamValue: string) => {
    return (
      Array.isArray(filteredData.courseStreamFilter) &&
      filteredData.courseStreamFilter.includes(streamValue)
    );
  };

  const closeSidebar = () => {
    setOpen(false);
    document.body.classList.remove("overflow-hidden");
  };

  return (
    <div
      className={`pt-[18px] px-4 sm:px-8 bg-white fixed top-0 w-full max-w-[605px] h-screen duration-300 ease-linear z-60 ${
        open ? "left-0" : "-left-full"
      }`}
    >
      <div className="flex items-center justify-between">
        <p className="text-lg sm:text-xl md:text-2xl lg:text-[28px] font-semibold leading-[32px] text-black">
          Filters
        </p>
        <span className="cursor-pointer" onClick={closeSidebar}>
          <Close />
        </span>
      </div>
      <div className="overflow-y-auto h-[calc(100vh-50px)] py-[20px] sm:py-[25px] md:py-[30px] lg:py-[35px] xl:py-[44px] scrollBarHidden">
        {from !== "search" && (
          <>
            <div className="flex items-center gap-4 ">
              <PreferencesMenu />
              <p className="text-black font-semibold text-base md:text-lg lg:text-xl xl:text-2xl ff_poppins leading-9">
                Educational Preferences
              </p>
            </div>
            {/* SELECT COUNTRY */}

            <p className="ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-[#727272] mt-4 sm:mt-5 md:mt-6">
              Country
            </p>
             <div className="flex flex-wrap gap-4 mt-4 mb-6">
               {/* COUNTRY LIST  */}
               {newCountry && newCountry.length > 0 ? (
                 newCountry.map((item) => (
                   <button
                     onClick={() => {
                       closeSidebar();
                       dispatch(setAiCourseFinderCountry(item.title));
                       setPage?.(1);
                       resetPage();
                     }}
                     key={item.title}
                     className={`cursor-pointer capitalize ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold  rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
                       country === item.title ? "bg-[#9EB2DE]" : "bg-[#F3F4F6]"
                     }`}
                   >
                     {item.title.replace("europe - ", "")}
                   </button>
                 ))
               ) : (
                 <SkeletonPills />
               )}
             </div>
          </>
        )}

        {/* COURSE PREFERENCE */}
        <div className="flex items-center gap-4">
          <span className="max-w-[24px] sm:max-w-[36px]">
            <EducationCap />
          </span>
          <p className="text-black font-semibold text-base md:text-lg lg:text-xl xl:text-2xl ff_poppins leading-9">
            Course Preferences
          </p>
        </div>

        {/* STUDY LEVEL */}
        {showLevel && (
          <>
            <p className="ff_poppins text-lg leading-[27px] text-[#727272] mt-6">
              Study Level
            </p>
            <div className="flex flex-wrap gap-4 mt-4">
              <button
                onClick={() => {
                  closeSidebar();
                  setFilteredData({
                    ...filteredData,
                    courseLevelFilter: "All",
                  });
                  setPage?.(1);
                  resetPage();
                }}
                className={`cursor-pointer ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
                  filteredData.courseLevelFilter === "All"
                    ? "bg-[#9EB2DE]"
                    : "bg-[#F3F4F6]"
                }`}
              >
                All
              </button>

              {courseFilterOptions && courseFilterOptions.length > 0 ? (
                courseFilterOptions.map((item) => (
                  <button
                    onClick={() => {
                      closeSidebar();
                      setFilteredData({
                        ...filteredData,
                        courseLevelFilter: item.course_level,
                      });
                      setPage?.(1);
                      resetPage();
                    }}
                    key={item.course_level}
                    className={`cursor-pointer ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold  rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
                      filteredData.courseLevelFilter === item.course_level
                        ? "bg-[#9EB2DE]"
                        : "bg-[#F3F4F6]"
                    }`}
                  >
                    {item.course_level}
                  </button>
                ))
              ) : (
                <SkeletonPills />
              )}
            </div>
          </>
        )}
        {/* scholarship */}
        {showScholarShip && (
          <>
            <p className="ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-[#727272] mt-6">
              scholarship
            </p>

            <div className="flex flex-wrap gap-4 mt-4">
              <button
                onClick={() => {
                  closeSidebar();
                  setFilteredData({
                    ...filteredData,
                    scholarshipTypeFilter: "All",
                  });
                  setPage?.(1);
                  resetPage();
                }}
                className={`cursor-pointer ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold  rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
                  filteredData.scholarshipTypeFilter === "All"
                    ? "bg-[#9EB2DE]"
                    : "bg-[#F3F4F6]"
                }`}
              >
                All
              </button>
              {scholarshipOptions && scholarshipOptions.length > 0 ? (
                scholarshipOptions.map((item, index) => (
                  <button
                    onClick={() => {
                      closeSidebar();
                      setFilteredData({
                        ...filteredData,
                        scholarshipTypeFilter: item.value,
                      });
                      setPage?.(1);
                      resetPage();
                    }}
                    key={index}
                    className={`ff_poppins cursor-pointer text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold  rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
                      filteredData.scholarshipTypeFilter === item.value
                        ? "bg-[#9EB2DE]"
                        : "bg-[#F3F4F6]"
                    }`}
                  >
                    {item.title}
                  </button>
                ))
              ) : (
                <SkeletonPills />
              )}
            </div>
          </>
        )}

        {/* INTAKE  */}
        <p className="ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-[#727272] mt-6">
          Intake
        </p>
        <div className="flex flex-wrap gap-4 mt-4">
          <button
            onClick={() => {
              closeSidebar();
              setFilteredData({
                ...filteredData,
                intake: "All",
              });
              setPage?.(1);
              resetPage();
            }}
            className={`cursor-pointer ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold  rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
              filteredData.intake === "All" ? "bg-[#9EB2DE]" : "bg-[#F3F4F6]"
            }`}
          >
            All
          </button>
          {newIntakeOption && newIntakeOption.length > 0 ? (
            newIntakeOption.map(
              (item) =>
                item.title && (
                  <button
                    onClick={() => {
                      closeSidebar();
                      setFilteredData({ ...filteredData, intake: item.id });
                      setPage?.(1);
                      resetPage();
                    }}
                    key={item.id}
                    className={`ff_poppins cursor-pointer text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold  rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
                      filteredData.intake === item.id
                        ? "bg-[#9EB2DE]"
                        : "bg-[#F3F4F6]"
                    }`}
                  >
                    {item.title}
                  </button>
                )
            )
          ) : (
            <SkeletonPills />
          )}
        </div>

        {/* STUDY LEVEL */}
        {isUniversityTab && (
          <>
            <p className="ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-[#727272] mt-6">
              Specialization
            </p>
            <div className="flex flex-wrap gap-4 mt-4">
              <button
                onClick={() => {
                  closeSidebar();
                  setFilteredData({
                    ...filteredData,
                    specialisationCategoryFilter: "All",
                  });
                  setPage?.(1);
                  resetPage();
                }}
                className={`cursor-pointer ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold  rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
                  filteredData.specialisationCategoryFilter === "All"
                    ? "bg-[#9EB2DE]"
                    : "bg-[#F3F4F6]"
                }`}
              >
                All
              </button>

              {specialisationCategory && specialisationCategory.length > 0 ? (
                specialisationCategory.map((item, idx) => (
                  <button
                    onClick={() => {
                      closeSidebar();
                      setFilteredData({
                        ...filteredData,
                        specialisationCategoryFilter: item,
                      });
                      setPage?.(1);
                      resetPage();
                    }}
                    key={idx}
                    className={`cursor-pointer ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold  rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
                      filteredData.specialisationCategoryFilter === item
                        ? "bg-[#9EB2DE]"
                        : "bg-[#F3F4F6]"
                    }`}
                  >
                    {capitalFirstLetter(item)}
                  </button>
                ))
              ) : (
                <SkeletonPills />
              )}
            </div>
          </>
        )}

        {/* COURSE STREAM - MODIFIED FOR MULTIPLE SELECTION */}
        {activeTab === "courses" && (
          <>
            <p className="ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-[#727272] mt-6">
              Course Stream
            </p>
            <div className="flex flex-wrap gap-4 mt-4">
              {courseStream && courseStream.length > 0 ? (
                courseStream.map((item) => (
                  <button
                    onClick={() => handleStreamSelection(item.course_stream)}
                    key={item.course_stream}
                    className={`cursor-pointer ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold  rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
                      isStreamSelected(item.course_stream)
                        ? "bg-[#9EB2DE]"
                        : "bg-[#F3F4F6]"
                    }`}
                  >
                    {item.course_stream}
                  </button>
                ))
              ) : (
                <SkeletonPills />
              )}
            </div>
          </>
        )}
        {/* STUDY LEVEL */}
        {activeTab === "courses" && (
          <>
            <div className="max-w-[485px] mx-auto sm:mx-0 w-full mt-12">
              <SelectInput
                value={filteredData.courseSpecialisationFilter}
                fieldName="specialisationCategory"
                title="Select Specialisation"
                content={newCourseSpecialisationOptions}
                onChange={(value) => {
                  closeSidebar();
                  setFilteredData({
                    ...filteredData,
                    courseSpecialisationFilter: value,
                  });
                  setPage?.(1);
                  resetPage();
                }}
                fromProfile={true}
              />
            </div>
          </>
        )}
        {/* STUDY LEVEL */}
        {activeTab === "courses" && (
          <>
            <p className="ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-[#727272] mt-6">
              Duration
            </p>
            <div className="flex flex-wrap gap-4 mt-4">
              <button
                onClick={() => {
                  closeSidebar();
                  setFilteredData({
                    ...filteredData,
                    courseDurationFilter: "All",
                  });
                  setPage?.(1);
                  resetPage();
                }}
                className={`cursor-pointer ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold  rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
                  filteredData.courseDurationFilter === "All"
                    ? "bg-[#9EB2DE]"
                    : "bg-[#F3F4F6]"
                }`}
              >
                All
              </button>

              {newCourseDuration && newCourseDuration.length > 0 ? (
                newCourseDuration.map((item) => (
                  <button
                    onClick={() => {
                      closeSidebar();
                      setFilteredData({
                        ...filteredData,
                        courseDurationFilter: item.title,
                      });
                      setPage?.(1);
                      resetPage();
                    }}
                    key={item.id}
                    className={`cursor-pointer ff_poppins text-sm sm:text-base md:text-lg leading-[27px] text-black font-semibold  rounded-full py-[6px] sm:py-[10px] px-3 sm:px-5 duration-300 ease-in-out ${
                      filteredData.courseDurationFilter === item.title
                        ? "bg-[#9EB2DE]"
                        : "bg-[#F3F4F6]"
                    }`}
                  >
                    {item.title}
                  </button>
                ))
              ) : (
                <SkeletonPills />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FilterSideBar;
